import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import InputField from "../../../FormComponents/InputField";

// Import icons
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "auto",
  minWidth: 400,
  maxWidth: "90%",
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 8px 24px 0 rgba(0,0,0,0.1)",
  padding: theme.spacing(3),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(2),
  boxShadow: "0 4px 16px 0 rgba(0,0,0,0.08)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.08)",
  marginLeft: theme.spacing(1),
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(0.5),
  display: "flex",
  alignItems: "center",
  "& svg": {
    fontSize: "1rem",
    marginRight: theme.spacing(0.75),
    color: theme.palette.primary.main,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  minWidth: "100%",
  "& .MuiInputBase-root": {
    borderRadius: theme.shape.borderRadius * 1.5,
  },
}));

export default function Ex_ItemUnitPrice({
  enableSetting,
  setEnableSetting,
  row,
  handleMethodChange,
  handleValueChange,
  handleFromDateChange,
  handleToDateChange,
  duration,
  enableDefine,
  setEnableDefine,
  setEnableDuration,
  enableDuration,
  handleQtyChange,
}) {
  const [selectedMethod, setSelectedMethod] = useState(row?.unitPriceMethod || "");
  const [unitPriceVal, setUnitPriceVal] = useState(row?.unitPrice || 0);
  const [fromDate, setFromDate] = useState(row?.fromDate || "");
  const [toDate, setToDate] = useState(row?.toDate || "");
  const [itemQty, setItemQty] = useState(row?.itemQty || 0);
  const [hasChanges, setHasChanges] = useState(false);

  const handleClose = () => {
    setEnableSetting(false);
    setSelectedMethod(null);
    setUnitPriceVal(0);
    setItemQty(0);
    setFromDate(null);
    setToDate(null);
    setHasChanges(false);
  };

  const handleSave = () => {
    // Here you would typically save all changes at once
    // For now, we'll just close the modal
    setEnableSetting(false);
    setHasChanges(false);
  };

  // Helper to track changes
  const updateHasChanges = () => {
    setHasChanges(true);
  };

  return (
    <Modal
      open={enableSetting}
      onClose={handleClose}
      aria-labelledby="unit-price-modal-title"
      aria-describedby="unit-price-modal-description"
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <StyledPaper elevation={3}>
        <HeaderCard>
          <Grid container alignItems="center" spacing={2}>
            <Grid item>
              <PriceChangeIcon fontSize="large" />
            </Grid>
            <Grid item xs>
              <Typography variant="h6" fontWeight="bold">
                Item Unit Price Settings
              </Typography>
              <Typography variant="body2">
                Configure pricing method and quantity
              </Typography>
            </Grid>
          </Grid>
        </HeaderCard>

        <Box sx={{ mb: 3 }}>
          <StyledFormControl size="small">
            <InputLabel id="unit-price-method-label">Unit Price Method</InputLabel>
            <Select
              labelId="unit-price-method-label"
              id="unit-price-method"
              name="_unitPriceMethod"
              value={selectedMethod || row.unitPriceMethod}
              label="Unit Price Method"
              onChange={(event) => {
                setSelectedMethod(event.target.value);
                handleMethodChange(event, row.importId, event.target.value);
                updateHasChanges();
                
                if (
                  event.target.value === "topMost" ||
                  event.target.value === "average"
                ) {
                  setEnableDuration(true);
                  setFromDate(null);
                  setToDate(null);
                  setEnableDefine(false);
                } else {
                  setEnableDuration(false);
                  setEnableDefine(true);
                }
              }}
            >
              <MenuItem value={"define"}>Define</MenuItem>
              <MenuItem value={"topMost"} disabled={true}>
                Top Most
              </MenuItem>
              <MenuItem value={"average"} disabled={true}>
                Average
              </MenuItem>
            </Select>
          </StyledFormControl>
        </Box>

        <Divider sx={{ my: 2 }} />

        {enableDefine && (
          <Box sx={{ mb: 3 }}>
            <InfoLabel>
              <AttachMoneyIcon /> Define Unit Price
            </InfoLabel>
            <InputField
              name="_total"
              value={unitPriceVal || row.unitPrice}
              onChange={(event, newInputValue) => {
                setUnitPriceVal(newInputValue);
                handleValueChange(
                  event,
                  row.importId,
                  parseFloat(newInputValue)
                );
                updateHasChanges();
              }}
              type="text"
              label="Unit Price"
              fullWidth
            />
          </Box>
        )}

        {enableDuration && (
          <Box sx={{ mb: 3 }}>
            <InfoLabel>
              <CalendarTodayIcon /> Select Date Range
            </InfoLabel>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <StyledFormControl size="small">
                  <InputLabel id="from-date-label">From Date</InputLabel>
                  <Select
                    labelId="from-date-label"
                    id="from-date"
                    name="_fromDate"
                    value={fromDate || row.fromDate}
                    label="From Date"
                    onChange={(event) => {
                      setFromDate(event.target.value);
                      handleFromDateChange(
                        event,
                        row.importId,
                        event.target.value
                      );
                      updateHasChanges();
                    }}
                  >
                    {duration.map((item, index) => (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledFormControl size="small">
                  <InputLabel id="to-date-label">To Date</InputLabel>
                  <Select
                    labelId="to-date-label"
                    id="to-date"
                    name="_toDate"
                    value={toDate || row.toDate}
                    label="To Date"
                    onChange={(event) => {
                      setToDate(event.target.value);
                      handleToDateChange(
                        event,
                        row.importId,
                        event.target.value
                      );
                      updateHasChanges();
                    }}
                  >
                    {duration.map((item, index) => (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </StyledFormControl>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <InfoLabel>
            <ShoppingCartIcon /> Required Quantity
          </InfoLabel>
          <InputField
            name="_qty"
            value={itemQty || row.itemQty}
            onChange={(event, newInputValue) => {
              setItemQty(newInputValue);
              handleQtyChange(event, row.importId, parseInt(newInputValue));
              updateHasChanges();
            }}
            type="number"
            label="Required Qty."
            fullWidth
          />
        </Box>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <ActionButton
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleClose}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes
          </ActionButton>
        </Stack>
      </StyledPaper>
    </Modal>
  );
}